import type { AddressViewModel } from 'src/api/types/AddressViewModel';
import type { PolicyAddressViewModel } from 'src/api/types/PolicyAddressViewModel';
import type { PolicyEmployeeLeasingPolicyTypeViewModel } from 'src/api/types/PolicyEmployeeLeasingPolicyTypeViewModel';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Card, Divider, Tooltip, CardContent } from '@mui/material';

import { AddressTypeEnum } from 'src/api/types/AddressTypeEnum';

import { EmptyContent } from 'src/components/empty-content';

import { StyledTooltipLabel } from '../styles/sharedStyles';
import DynamicBreadcrumbs from '../../dynamic-breadcrumbs/dynamic-breadcrumbs';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getDefaultAddressByPolicy = (
  policyAddresses: PolicyAddressViewModel[]
): string | undefined => {
  const defaultAddress = policyAddresses?.find(
    (address) => address.address?.addressTypeEnum == AddressTypeEnum.INSURED
  )?.address;

  return defaultAddress ? getAddressToString(defaultAddress) : '';
};

export const formatZipcode = (zipcode: string): string => {
  const trimmedZipcode = zipcode?.trim() || '';
  // Add hyphen after 5th digit if it's exactly 9 digits
  return /^\d{9}$/.test(trimmedZipcode)
    ? `${trimmedZipcode.slice(0, 5)}-${trimmedZipcode.slice(5)}`
    : trimmedZipcode;
};

export const getAddressToString = (policyAddresses: AddressViewModel | undefined): string => {
  if (!policyAddresses) return '';

  const street = policyAddresses?.street?.trim() || '';
  const city = policyAddresses?.city?.trim() || '';
  const state = policyAddresses?.state?.trim() || '';
  const zipcode = policyAddresses?.zipcode || '';

  const formattedZipcode = formatZipcode(zipcode);

  // Build address parts conditionally
  const parts: string[] = [];

  if (street) {
    parts.push(street);
  }

  // Build second line with city, state, and zipcode
  const secondLineParts: string[] = [];
  if (city) {
    secondLineParts.push(city);
  }
  if (state || formattedZipcode) {
    const stateZip = [state, formattedZipcode].filter(Boolean).join('\u00A0\u00A0');
    if (stateZip) {
      secondLineParts.push(stateZip);
    }
  }

  if (secondLineParts.length > 0) {
    parts.push(secondLineParts.join(', '));
  }

  return parts.join(', \n');
};

export const formatDate = (dateString: Date | string | null | undefined): string => {
  if (!dateString) return '';
  // Parse as UTC and format in UTC to avoid timezone conversions
  const date = dayjs.utc(dateString);
  return date.format('MM/DD/YYYY');
};

export const formatDollarAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '';
  const roundedAmount = Math.round(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(roundedAmount);
};

export function showError(pageTitle: any, errorMessage: string) {
  return (
    <Box>
      <Box sx={{}}>
        <Typography variant="h4">{pageTitle}</Typography>
        <DynamicBreadcrumbs crumbStartPath="combo" />
        <Divider />
        <Card
          sx={{
            display: 'flex',
            zIndex: 'unset',
            overflow: 'unset',
            mt: 3,
            borderRadius: 2,
            boxShadow: 'var(--customShadows-card)',
            width: '75%',
          }}
        >
          <CardContent sx={{ flexGrow: 1, pr: 1.5 }} data-testid="coverages-card-content">
            <EmptyContent
              title="The requested Url is Invalid."
              description={errorMessage}
              sx={{ pb: 10, pt: 5 }}
              data-testid="combo-coverages-empty-content"
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export const mapCorrectionTypeCodeToDescription = (code: string | null | undefined): string => {
  switch (code) {
    case 'L':
      return 'Loss';
    case 'H':
      return 'Header';
    case 'M':
      return 'Multiple';
    case 'E':
      return 'Exposure';
    case 'T':
      return 'Total';
    default:
      return '';
  }
};

// Map your custom event color to MUI TimelineDot color
export const mapEventColorToMui = (
  color: string | undefined
):
  | 'error'
  | 'inherit'
  | 'success'
  | 'info'
  | 'warning'
  | 'grey'
  | 'primary'
  | 'secondary'
  | undefined => {
  switch (color) {
    case 'Error':
      return 'error';
    case 'Success':
      return 'success';
    case 'Info':
      return 'info';
    case 'Warning':
      return 'warning';
    case 'Grey':
      return 'grey';
    case 'Primary':
      return 'primary';
    case 'Secondary':
      return 'secondary';
    default:
      return 'grey';
  }
};

export const mapEstimatedAuditCodeToDescription = (code: string | null | undefined): string => {
  switch (code) {
    case 'N':
      return 'No';
    case 'Y':
      return 'Yes';
    case 'U':
      return 'Uncooperative';
    default:
      return '';
  }
};

export const mapPlanTypeCodeToDescription = (code: string | null | undefined): string => {
  switch (code) {
    case '01':
      return 'Voluntary';
    case '02':
      return 'Assigned Risk';
    case '03':
    case '04':
    case '05':
    case '06':
      return 'Invalid';
    case '07':
      return 'Assigned Risk - Other DCO';
    default:
      return '';
  }
};

export const renderEmployeeLeasingTypeWithTooltip = (
  policyEmployeeLeasingPolicyType?: PolicyEmployeeLeasingPolicyTypeViewModel
) => {
  const categoryType = policyEmployeeLeasingPolicyType?.employeeLeasingPolicyTypeCategory;
  const subCategoryType = policyEmployeeLeasingPolicyType?.employeeLeasingPolicyTypeTooltip;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
      {subCategoryType ? (
        <Box sx={{ alignItems: 'center' }}>
          <Tooltip arrow title={subCategoryType} placement="top">
            <StyledTooltipLabel data-testid="employee-leasing-type-label">
              <Typography component="span" sx={{ typography: 'body2', color: 'text.secondary' }}>
                Employee leasing type
              </Typography>
            </StyledTooltipLabel>
          </Tooltip>
        </Box>
      ) : (
        <Typography component="span" sx={{ typography: 'body2', color: 'text.secondary' }}>
          Employee leasing type
        </Typography>
      )}
      <Typography
        component="span"
        sx={{ typography: 'subtitle2' }}
        data-testid="employee-leasing-type-value"
      >
        {categoryType || ''}
      </Typography>
    </Box>
  );
};
